import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Upload, Check, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_OPTIONS = [
  { value: "express_post", label: "Express Post", fee: 12 },
  { value: "own_courier", label: "Own Courier / Driver", fee: 0 },
  { value: "urgent_express", label: "URGENT + Express Post", fee: 32 },
  { value: "urgent_courier", label: "URGENT + Own Courier", fee: 20 },
  { value: "pickup", label: "Pickup from Amcal Newmarket", fee: 0 },
];

export default function QuoteRequestPage() {
  const navigate = useNavigate();
  const { activeOrgId, activeOrg } = useAuth();
  const [file, setFile] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("express_post");
  const [notes, setNotes] = useState("");
  const [patientName, setPatientName] = useState("");
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const fetchPatientSuggestions = async (q) => {
    try { const { data } = await axios.get(`${API}/patients/search?q=${encodeURIComponent(q)}`, { withCredentials: true }); setPatientSuggestions(data.patients); } catch { /* */ }
  };

  const handleFileChange = (e) => {
    if (e.target.files?.length) setFile(e.target.files[0]);
  };

  const handleSubmit = async () => {
    if (!file) { toast.error("Please upload a prescription/script"); return; }
    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("delivery_method", deliveryMethod);
    formData.append("notes", notes);
    formData.append("patient_name", patientName);
    if (activeOrgId) formData.append("org_id", activeOrgId);
    const verifiedAddr = (activeOrg?.addresses || []).find(a => a.verified);
    if (verifiedAddr) formData.append("address_id", verifiedAddr.id);
    try {
      const { data } = await axios.post(`${API}/orders/quote-request`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSuccess(data);
      toast.success("Quote request submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit quote request");
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="quote-success">
        <div className="border border-emerald-200 bg-emerald-50 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500 flex items-center justify-center mx-auto mb-4">
            <Check size={32} weight="bold" className="text-white" />
          </div>
          <h2 className="font-[Chivo] text-2xl font-black text-zinc-950">Quote Request Submitted</h2>
          <p className="text-sm text-zinc-600 mt-2">We'll review your script and get back to you with pricing.</p>
          {success.eta && (
            <div className="mt-4 p-3 bg-white border border-emerald-200">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">TIMELINE</p>
              <p className="font-semibold text-zinc-950 mt-1">{success.eta.message}</p>
              {success.eta.date && <p className="text-xs text-zinc-500 mt-1">Estimated ready: {success.eta.date}</p>}
            </div>
          )}
          <p className="text-xs text-zinc-400 mt-3">Order #{success.id?.slice(0, 8)}</p>
          <div className="flex gap-3 mt-6 justify-center">
            <button onClick={() => navigate("/orders")} className="px-5 py-2.5 text-sm font-semibold bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors" data-testid="view-orders-button">
              View Orders
            </button>
            <button onClick={() => { setSuccess(null); setFile(null); setNotes(""); }} className="px-5 py-2.5 text-sm font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200 transition-colors" data-testid="submit-another-button">
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="quote-request-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">QUICK ORDER</p>
        <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Request a Quote</h1>
        <p className="text-sm text-zinc-500 mt-2">Upload your prescription and we'll price it for you. No need to browse the catalog.</p>
      </div>

      <div className="space-y-5">
        {/* Patient Name */}
        <div>
          <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Patient Name (optional)</Label>
          <div className="relative">
            <Input
              value={patientName}
              onChange={e => { setPatientName(e.target.value); if (e.target.value.length >= 2) fetchPatientSuggestions(e.target.value); else setPatientSuggestions([]); }}
              className="mt-1.5 rounded-none"
              placeholder="Start typing to search previous patients..."
              data-testid="quote-patient-name"
              autoComplete="off"
            />
            {patientSuggestions.length > 0 && (
              <div className="absolute z-20 w-full border border-zinc-200 bg-white shadow-lg mt-0.5 max-h-40 overflow-y-auto">
                {patientSuggestions.map((p, i) => (
                  <button key={i} onClick={() => { setPatientName(p.name); setPatientSuggestions([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex justify-between">
                    <span className="text-zinc-800 font-medium">{p.name}</span>
                    <span className="text-xs text-zinc-400">{p.order_count} orders</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Script Upload */}
        <div>
          <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Prescription / Script *</Label>
          <label className={`mt-1.5 border-2 border-dashed p-10 text-center cursor-pointer block transition-colors ${
            file ? "border-emerald-400 bg-emerald-50/30" : "border-zinc-300 hover:border-[#0055FF] hover:bg-blue-50/30"
          }`} data-testid="quote-upload-area">
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <Check size={20} className="text-emerald-600" />
                <span className="text-sm font-medium text-zinc-800">{file.name}</span>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto text-zinc-400 mb-2" />
                <p className="text-sm text-zinc-600 font-medium">Click to upload prescription photo</p>
                <p className="text-xs text-zinc-400 mt-1">JPG, PNG, PDF up to 10MB</p>
              </>
            )}
            <input type="file" accept="image/*,.pdf" onChange={handleFileChange} className="hidden" data-testid="quote-file-input" />
          </label>
        </div>

        {/* Delivery */}
        <div>
          <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Delivery Method</Label>
          <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
            <SelectTrigger className="mt-1.5 rounded-none" data-testid="quote-delivery-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_OPTIONS.map(d => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label} {d.fee > 0 ? `($${d.fee})` : "(Free)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="mt-1.5 rounded-none" placeholder="Any special instructions..." data-testid="quote-notes-input" />
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || !file}
          className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="submit-quote-button"
        >
          {submitting ? "SUBMITTING..." : "SUBMIT QUOTE REQUEST"} <ArrowRight size={16} />
        </button>
      </div>

      <div className="mt-8 border border-zinc-200 p-4 bg-zinc-50">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">HOW IT WORKS</p>
        <div className="space-y-2 text-sm text-zinc-600">
          <p>1. Upload your prescription photo</p>
          <p>2. We review and price the items</p>
          <p>3. You receive a notification with the quoted price</p>
          <p>4. We compound and ship your order</p>
        </div>
      </div>
    </div>
  );
}
